<template>
  <div :style="'width:'+maxWidth+'; height:'+maxHeight+'; display: flex; justify-content: center; align-items:center;'">
    <v-progress-circular
        v-if="loadingWebcam"
        :width="1"
        indeterminate
    ></v-progress-circular>
    <video v-show="!loadingWebcam && hasWebcamAccess"
           :id="webcamId"
           ref="videoElement"
           :width="maxWidth"
           :height="maxHeight"
    ></video>
    <canvas v-if="detectFace" style="display: none" ref="canvasElement"></canvas>
    <div v-if="!loadingWebcam && !hasWebcamAccess && handleErrors"
         style="width: 100%; display: flex; flex-direction: column; align-items: center; justify-content: space-around; flex-wrap: wrap;"
         class="text-center pa-3">
      <v-icon size="45">mdi-close-circle-outline</v-icon>
      <h3 class="font-weight-regular mt-2">{{$t('general.webcam_problem')}}</h3>
    </div>
  </div>
</template>

<script>
  import * as faceapi from 'face-api.js';
  import {throttle} from "../../lib/rate";
  import DiffCamEngine from '../../lib/motionDetector';

  export default {
    name: "Webcam",
    props: {
      webcamId: {
        required: true,
        type: String,
      },
      autoStream: {
        default: true,
        type: Boolean
      },
      handleErrors: {
        default: true,
        type: Boolean
      },
      maxHeight: {
        type: String,
        default: '100%'
      },
      maxWidth: {
        type: String,
        default: '100%'
      },
      detectFace: {
        type: Boolean,
        default: false
      },
      detectMotion: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        loadingWebcam: true,
        hasWebcamAccess: false,
        video: null,
        selectedVideoDevice: null,
        hasInit: false,
        faceModelsLoaded:false,
        faceDetections : [],
        motionDetectionCount:0,
        motionDetectionInitiated:false
      }
    },
    watch: {
      '$store.state.media.cameraDevice': function () {

        // if(!this.hasInit){
        //   this.hasInit = true;
        //   return;
        // }

        this.initVideoDevice();
      }
    },
    methods: {
      startVideoStream() {

        if (this.selectedVideoDevice == null) {
          this.$nextTick(() => {
            this.hasWebcamAccess = false;
          });
          this.$emit('lostWebcamAccess');
          return;
        }

        if (this.video == null)
          this.video = this.$refs.videoElement;

        if (navigator.mediaDevices.getUserMedia) {
          navigator.mediaDevices.getUserMedia({
            video: {
              deviceId: this.selectedVideoDevice,
            }
          })
            .then((stream) => {
              this.video.srcObject = stream;
              this.$emit('videoRef', this.$refs.videoElement);
              this.video.play();
              this.startFaceDetection();
            })
            .catch(() => {
              this.hasWebcamAccess = false;
              this.stopVideoStream();
            });
        }
      },
      stopVideoStream() {
        let stream = null;

        if (this.video != null)
          stream = this.video.srcObject;

        if (stream != null) {
          let tracks = stream.getTracks();
          tracks.forEach(t => t.stop());
          this.video.srcObject = null;
          this.video = null;
        }
      },
      initVideoDevice(reset = false) {

        //A reset happens when the navigator picks up that the devices changed (like on closing the lid)
        if (!reset)
          this.selectedVideoDevice = this.$store.state.media.cameraDevice;
        else
          this.selectedVideoDevice = null;

        if (this.selectedVideoDevice != null) {
          this.hasWebcamAccess = true;
          this.$emit('gainedWebcamAccess');
          if (this.autoStream) {
            this.startVideoStream();
          }
          this.startMotionDetection();
          setTimeout(() => {
            this.loadingWebcam = false;
            this.$emit('ready');
          }, 400);
          return;
        }

        navigator.mediaDevices != null && navigator.mediaDevices.enumerateDevices()
          .then((devices) => {

            let possibleVideoDevices = devices.filter(d => d.kind === 'videoinput');
            let defaultVideoInput = possibleVideoDevices.findIndex(d => d.deviceId === 'default');
            if (defaultVideoInput >= 0) {
              this.setCameraDevice(possibleVideoDevices[defaultVideoInput].deviceId);
              this.hasWebcamAccess = true;
              this.startMotionDetection();
              this.$emit('gainedWebcamAccess');
            } else if (possibleVideoDevices.length === 1) {
              this.setCameraDevice(possibleVideoDevices[0].deviceId);
              this.hasWebcamAccess = true;
              this.startMotionDetection();
              this.$emit('gainedWebcamAccess');
            } else {
              //No video input available
              this.hasWebcamAccess = false;
              this.$emit('lostWebcamAccess');
            }

            setTimeout(() => {
              this.loadingWebcam = false;
              this.$emit('ready');
            }, 400);

          })
      },
      setCameraDevice(val) {
        this.$store.dispatch('media/setCameraDevice', val);
      },
      initMotionDetection(){
        this.$watch('detectMotion',()=>{
          if(this.detectMotion){
            this.startMotionDetection();
          }else{
            this.stopMotionDetection();
          }
        });
      },
      startMotionDetection(){
        if(!this.detectMotion){
          return;
        }
       this.motionDetectionCount=0;
        this.motionDetectionInitiated=false;
        DiffCamEngine.init({
          video: this.$refs.videoElement,
          captureIntervalTime: 100,
          captureCallback: (e)=>{
            if(e.hasMotion){
              this.motionDetectionCount++;
            }
            if(this.motionDetectionCount >= 5){
              this.motionDetectionCount=0;
              this.$emit('motion-detected',e);
            }
          },
          initSuccessCallback:()=>{
            if(this.detectMotion){
              this.motionDetectionInitiated=true;
              DiffCamEngine.start();
            }
          }
        });
      },
      stopMotionDetection(){
        if(this.motionDetectionInitiated){
          DiffCamEngine.stop();
          this.motionDetectionInitiated=false;
          this.motionDetectionCount=0;
        }
      },
      initFaceDetection(){
        const init = ()=>{
          return faceapi.nets.tinyFaceDetector.loadFromUri('/weights').then(()=>{
            this.faceModelsLoaded=true;
          });
        };
        if(this.detectFace){
          init();
        }
        this.$watch('detectFace',()=>{
          if(this.detectFace){
            init().then(()=>this.startFaceDetection());
          }
        });
      },
      startFaceDetection(){
        if(!this.detectFace) return;
        let canvasEl = this.$refs.canvasElement;
        const videoEl = this.$refs.videoElement;
        let emptyCount=0;
        let detect;
        const maxFps = 3;

        let prevDetectionSize=-1;
        const emit = throttle(()=>{
          if(this.faceDetections.length===0){
            this.$emit('no-faces-detected');
          }else if(prevDetectionSize!==this.faceDetections.length){
            prevDetectionSize=this.faceDetections.length;
            this.$emit('faces-detected',this.faceDetections);
          }
        },500);
        const delayedFrame=(delayedBy)=>{
          setTimeout(() => {
            if(this.detectFace){
              requestAnimationFrame(detect);
            }
          }, delayedBy || (1000 / maxFps));
        };
        detect=()=>{
          if(this.faceModelsLoaded) {
            let ctx = canvasEl.getContext('2d');
            if(videoEl.offsetWidth > 0 && videoEl.offsetHeight > 0){
              canvasEl.width=videoEl.offsetWidth;
              canvasEl.height=videoEl.offsetHeight;
            }
            ctx.drawImage(videoEl, 0, 0, videoEl.offsetWidth, videoEl.offsetHeight);
            faceapi.detectAllFaces(canvasEl,new faceapi.TinyFaceDetectorOptions()).then((result) => {
              if(!result || result.length===0){
                emptyCount++;
                if(emptyCount>=2*(maxFps)){
                  emptyCount=0;
                  this.faceDetections=[];
                }
              }else{
                emptyCount=0;
                this.faceDetections=result;
              }
              emit();
              delayedFrame();
            }).catch((err)=>{
              console.error(err);
            });
          }else{
            delayedFrame(1000);
          }
        };
        detect();
      }
    },
    beforeDestroy() {
      this.stopMotionDetection();
      this.stopVideoStream();
    },
    beforeMount(){
      setTimeout(()=>{
        if (navigator.mediaDevices != null) {
          navigator.mediaDevices.ondevicechange = () => {
            this.$store.dispatch('media/setCameraDevice', null);
            this.initVideoDevice(true);
          }
        }

          navigator.permissions.query({ name: 'camera' }).then((notificationPerm)=>{
              notificationPerm.onchange = ()=>{
                  this.initVideoDevice(true);
              };
          });
      },1000);

    },
    mounted() {
      this.initVideoDevice();
      this.initMotionDetection();
    },
    created() {
      this.initFaceDetection();
    }
  }
</script>

<style scoped>

</style>
